import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  Sparkles,
  Mic,
  Wallet,
  TrendingUp,
  Users,
  Compass,
  Target,
  BookOpen,
  Globe2,
  Award,
  GraduationCap,
  Heart,
  Briefcase,
  Lightbulb,
  Megaphone,
  HandHeart,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import {
  ESS_LOGO_DARK,
  FOUNDER_BANNER,
  FOUNDER_PORTRAIT,
  FOUNDER_PORTRAIT_ALT,
  LIFESTYLE,
} from "@/lib/brand";
import {
  SectionHeader,
  EmeraldButton,
  GhostButton,
  GoldButton,
} from "@/components/Bits";

const services = [
  {
    icon: Sparkles,
    title: "Digital Product Strategy",
    body: "Helping professionals transform knowledge and expertise into digital products designed to expand reach and create new opportunities.",
  },
  {
    icon: Clock,
    title: "Productivity & Time Freedom",
    body: "Educational strategies for reducing overwhelm, increasing focus, and creating intentional space for what matters most.",
  },
  {
    icon: Wallet,
    title: "Financial Wellness Education",
    body: "Educational resources that support stronger financial decision-making, money confidence, and long-term economic empowerment.",
  },
  {
    icon: TrendingUp,
    title: "Business Visibility & Growth",
    body: "Helping emerging entrepreneurs establish their brand presence, refine positioning, and grow their audience with integrity.",
  },
  {
    icon: Mic,
    title: "Speaking & Workshops",
    body: "Educational presentations for organizations, conferences, professional groups, and women-led communities.",
  },
];

const frameworks = [
  {
    name: "Digital Burnout Cycle™",
    body: "A developing model that maps the stages of digital overwhelm — so professionals can recognize the pattern and interrupt the loop before it costs their well-being.",
  },
  {
    name: "Digital Clarity Method™",
    body: "An emerging practice for reclaiming attention, reorganizing inputs, and rebuilding the deep focus that strategic work requires.",
  },
  {
    name: "RESET Framework™",
    body: "A proprietary path from exhaustion to exit strategy — Reflect, Eliminate, Strategize, Elevate, Transform.",
  },
];

const visionPanels = [
  {
    icon: Compass,
    label: "Vision",
    title: "Empowering Professionals Nationwide",
    body: "Supporting professionals as they build more intentional and fulfilling futures.",
  },
  {
    icon: Lightbulb,
    label: "Focus Area",
    title: "Digital Education & Productivity",
    body: "Providing practical resources that help professionals reclaim their time and attention.",
  },
  {
    icon: BookOpen,
    label: "Commitment",
    title: "Evidence-Based Personal Development",
    body: "Combining education, strategy, and real-world experience to support meaningful growth.",
  },
  {
    icon: Target,
    label: "Impact Goal",
    title: "Economic Empowerment Through Education",
    body: "Helping individuals create more options, opportunities, and confidence for their future.",
  },
];

const credentials = [
  { icon: Briefcase, kicker: "26+ Years", title: "Federal Service Experience" },
  { icon: GraduationCap, kicker: "PhD", title: "Public Health (Epidemiology)" },
  { icon: Heart, kicker: "Certified", title: "Health Coach" },
  { icon: Award, kicker: "Certified", title: "Master Life Coach" },
  { icon: Users, kicker: "Professional", title: "Administrative Management Specialist" },
  { icon: Sparkles, kicker: "Entrepreneur", title: "Digital Creator & Founder" },
  { icon: Megaphone, kicker: "Speaker", title: "Professional Development Educator" },
  { icon: HandHeart, kicker: "Mission-Driven", title: "Advocate for Economic Empowerment" },
];

const Home = () => {
  const [lead, setLead] = useState({ first_name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  const LEAD_MAGNET_URL =
    "https://drive.google.com/file/d/1IRByega2lAtU-15KuJpchpssEGkSzOs3/view?usp=drive_link";

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.first_name || !lead.email) {
      toast.error("Please complete both fields to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...lead, source: "digital_detox_blueprint" });
      // Open the complimentary resource in a new tab
      window.open(LEAD_MAGNET_URL, "_blank", "noopener,noreferrer");
      toast.success(
        "Welcome to the Society — your resource is opening in a new tab."
      );
      setLead({ first_name: "", email: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={FOUNDER_BANNER}
            alt="Dr. Phelesia Foster — The Exit Strategy Society"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ess-emerald via-ess-emerald/75 to-ess-emerald/10" />
        </div>

        {/* ESS Brand Mark — top right, blended */}
        <img
          src={ESS_LOGO_DARK}
          alt="ESS — The Exit Strategy Society"
          aria-hidden="true"
          className="hidden md:block absolute top-28 right-8 lg:right-16 w-36 lg:w-48 z-10 pointer-events-none"
          style={{ mixBlendMode: "screen", opacity: 0.92 }}
          data-testid="hero-ess-mark"
        />
        <div className="ess-container relative py-32 md:py-44">
          <div className="max-w-2xl ess-fade-in">
            <p className="ess-overline text-ess-gold">An Executive Society for Professionals</p>
            <h1 className="font-display text-white mt-5 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              Reclaim Your Time.
              <br />
              Build Your Freedom.
              <br />
              <span className="text-ess-gold italic">Design Your Next Chapter.</span>
            </h1>
            <p className="mt-7 text-white/85 text-lg leading-relaxed max-w-xl">
              The Exit Strategy Society helps busy professionals reduce overwhelm,
              increase productivity, create additional income opportunities, and
              build a strategic pathway toward greater freedom and fulfillment.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <EmeraldButton
                as="link"
                to="/services"
                data-testid="hero-start-journey"
                className="bg-ess-gold text-ess-charcoal hover:bg-white"
              >
                Start Your Journey <ArrowRight size={14} className="ml-3" />
              </EmeraldButton>
              <GhostButton
                as="link"
                to="/book"
                data-testid="hero-book-consultation"
                className="border-white text-white hover:bg-white hover:text-ess-emerald"
              >
                Book a Consultation
              </GhostButton>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ess-gold to-transparent" />
      </section>

      {/* FOUNDER INTRO */}
      <section className="ess-section bg-ess-paper">
        <div className="ess-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={FOUNDER_PORTRAIT}
                alt="Dr. Phelesia Foster, PhD"
                className="w-full object-cover shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-ess-emerald text-white p-6 max-w-[220px] hidden md:block">
                <p className="ess-overline text-ess-gold">Founder</p>
                <p className="font-display text-2xl mt-2 leading-tight">
                  Dr. Phelesia Foster, PhD
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <SectionHeader
              overline="Meet the Founder"
              title="A strategist for the professional ready to design what's next."
              subtitle="With more than 26 years of federal service experience, a PhD in Public Health (Epidemiology), and a portfolio of certifications spanning coaching, health, and digital education — Dr. Phelesia Foster is building a society for professionals ready to design lives aligned with their values."
            />
            <div className="mt-10 flex flex-wrap gap-4">
              <EmeraldButton as="link" to="/about" data-testid="home-about-cta">
                Read Her Story <ArrowRight size={14} className="ml-3" />
              </EmeraldButton>
            </div>
          </div>
        </div>
      </section>

      {/* WHY WORK WITH DR. FOSTER — CREDENTIALS */}
      <section className="ess-section bg-white" data-testid="home-credentials">
        <div className="ess-container">
          <SectionHeader
            overline="Credentials"
            title="Why Work With Dr. Phelesia Foster?"
            subtitle="The Exit Strategy Society is built upon decades of professional leadership, education, service, and personal development experience."
            align="center"
            maxWidth="max-w-3xl mx-auto"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ess-blush mt-14 border border-ess-blush">
            {credentials.map(({ icon: Icon, kicker, title }) => (
              <div
                key={title}
                className="bg-white p-7 group hover:bg-ess-paper transition-colors"
              >
                <div className="w-11 h-11 border border-ess-gold/40 flex items-center justify-center text-ess-gold-deep group-hover:bg-ess-emerald group-hover:text-ess-gold group-hover:border-ess-emerald transition-all">
                  <Icon size={18} strokeWidth={1.4} />
                </div>
                <p className="font-display text-2xl text-ess-emerald mt-5 leading-tight">
                  {kicker}
                </p>
                <p className="text-[13px] text-ess-charcoal/80 mt-2 leading-relaxed">
                  {title}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT — VISION PANELS (replaces metrics) */}
      <section
        data-testid="home-impact"
        className="bg-ess-blush ess-section"
      >
        <div className="ess-container">
          <div className="max-w-3xl">
            <p className="ess-overline">Impact & Community Development</p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl text-ess-emerald leading-tight">
              Creating Pathways to Economic Empowerment
            </h2>
            <span className="ess-divider mt-6" />
            <div className="mt-6 space-y-4 text-ess-slate leading-relaxed">
              <p>
                The Exit Strategy Society is committed to helping professionals
                reclaim their time, strengthen their confidence, expand their
                opportunities, and build sustainable pathways toward greater
                freedom and fulfillment.
              </p>
              <p>
                We believe education, personal development, digital literacy, and
                entrepreneurship can create life-changing opportunities for
                individuals and communities.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ess-emerald/15 mt-14">
            {visionPanels.map(({ icon: Icon, label, title, body }) => (
              <div
                key={label}
                data-testid={`vision-${label.replace(/\s+/g, "-").toLowerCase()}`}
                className="bg-ess-blush p-8 md:p-10 group"
              >
                <div className="w-12 h-12 border border-ess-emerald/20 flex items-center justify-center text-ess-emerald group-hover:bg-ess-emerald group-hover:text-ess-gold group-hover:border-ess-emerald transition-all">
                  <Icon size={20} strokeWidth={1.4} />
                </div>
                <p className="ess-overline mt-6">{label}</p>
                <h3 className="font-display text-2xl md:text-[26px] text-ess-emerald mt-3 leading-tight">
                  {title}
                </h3>
                <p className="text-ess-slate text-sm mt-3 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="ess-section bg-white">
        <div className="ess-container">
          <SectionHeader
            overline="The Services"
            title="Educational support for ambitious lives."
            subtitle="Five disciplines, one unifying purpose — help professionals build the life their work has earned them."
            align="center"
            maxWidth="max-w-3xl mx-auto"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ess-blush mt-14 border border-ess-blush">
            {services.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-white p-10 group hover:bg-ess-paper transition-colors"
                data-testid={`service-${title.toLowerCase().replace(/\W+/g, "-")}`}
              >
                <div className="w-12 h-12 border border-ess-gold/40 flex items-center justify-center text-ess-gold-deep group-hover:bg-ess-emerald group-hover:text-ess-gold group-hover:border-ess-emerald transition-all">
                  <Icon size={22} strokeWidth={1.4} />
                </div>
                <h3 className="font-display text-2xl mt-6 text-ess-emerald">{title}</h3>
                <p className="text-ess-slate mt-3 leading-relaxed">{body}</p>
                <Link
                  to="/services"
                  className="ess-link mt-6 inline-flex items-center text-[12px] uppercase tracking-[0.22em] text-ess-emerald"
                >
                  Learn more <ArrowRight size={12} className="ml-2" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIGNATURE FRAMEWORKS */}
      <section className="ess-section bg-ess-emerald text-white relative overflow-hidden">
        <div className="absolute inset-0 ess-grain opacity-20" />
        <div className="ess-container relative">
          <div className="max-w-3xl">
            <p className="ess-overline text-ess-gold">Signature Frameworks</p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl leading-tight">
              Proprietary methods <em className="text-ess-gold not-italic">earned</em> through 26 years of service.
            </h2>
            <span className="ess-divider mt-6" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 mt-14 border border-white/10">
            {frameworks.map((f, i) => (
              <div key={f.name} className="bg-ess-emerald p-10 hover:bg-ess-emerald-dark transition-colors">
                <p className="font-display text-7xl text-ess-gold/30 leading-none">
                  0{i + 1}
                </p>
                <h3 className="font-display text-3xl mt-4">{f.name}</h3>
                <p className="text-white/75 mt-4 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-white/60 text-sm italic max-w-3xl leading-relaxed">
            The Digital Burnout Cycle™, Digital Clarity Method™, and RESET Framework™
            represent emerging intellectual property concepts currently being developed
            and refined through The Exit Strategy Society's educational initiatives.
          </p>
        </div>
      </section>

      {/* WHY ESS EXISTS — FOUNDER MESSAGE (replaces testimonials) */}
      <section className="ess-section bg-ess-paper" data-testid="home-founder-message">
        <div className="ess-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 lg:order-2">
            <img
              src={FOUNDER_PORTRAIT}
              alt="Dr. Phelesia Foster"
              className="w-full aspect-[4/5] object-cover shadow-2xl"
            />
          </div>
          <div className="lg:col-span-7 lg:order-1">
            <p className="ess-overline">Why ESS Exists</p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl text-ess-emerald leading-tight">
              A Message From Dr. Phelesia Foster
            </h2>
            <span className="ess-divider mt-6" />
            <div className="mt-8 space-y-5 text-ess-charcoal/85 leading-relaxed">
              <p>
                After more than 26 years of federal service, I realized many
                professionals were successful on paper but overwhelmed in practice.
              </p>
              <p>
                Too many talented individuals were navigating burnout, digital
                overload, financial stress, and uncertainty about their next chapter.
              </p>
              <p>
                The Exit Strategy Society was created to help professionals reclaim
                their time, increase their confidence, expand their opportunities,
                and build lives aligned with their values.
              </p>
              <p className="font-display text-2xl text-ess-emerald italic leading-snug">
                My mission is simple: to help people create more options, more
                freedom, and more fulfillment through education, strategy, and
                personal growth.
              </p>
              <p>This is not about escaping life.</p>
              <p className="font-display text-xl text-ess-emerald italic">
                This is about intentionally designing it.
              </p>
            </div>
            <div className="mt-8 border-l-2 border-ess-gold pl-4">
              <p className="font-display text-xl text-ess-emerald leading-tight">
                — Dr. Phelesia Foster, PhD
              </p>
              <p className="ess-overline mt-1">
                Founder, The Exit Strategy Society
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LEAD MAGNET */}
      <section className="ess-section bg-ess-blush relative overflow-hidden">
        <div className="ess-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img
              src={LIFESTYLE.workspace}
              alt="Digital Detox Blueprint"
              className="w-full aspect-[4/5] object-cover shadow-xl"
            />
          </div>
          <div className="lg:col-span-7">
            <p className="ess-overline">Complimentary Resource</p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl text-ess-emerald leading-tight">
              Get your free Digital Productivity Resource.
            </h2>
            <p className="mt-5 text-ess-slate max-w-xl leading-relaxed">
              A handcrafted guide to help you interrupt digital overwhelm, restore
              your focus, and create more intentional space for what matters most.
            </p>
            <form
              onSubmit={submitLead}
              className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-2xl"
              data-testid="lead-magnet-form"
            >
              <input
                required
                type="text"
                placeholder="First Name"
                value={lead.first_name}
                onChange={(e) =>
                  setLead((l) => ({ ...l, first_name: e.target.value }))
                }
                data-testid="lead-first-name"
                className="bg-white px-5 py-4 text-sm border border-ess-blush-deep focus:border-ess-emerald outline-none transition"
              />
              <input
                required
                type="email"
                placeholder="Email Address"
                value={lead.email}
                onChange={(e) =>
                  setLead((l) => ({ ...l, email: e.target.value }))
                }
                data-testid="lead-email"
                className="bg-white px-5 py-4 text-sm border border-ess-blush-deep focus:border-ess-emerald outline-none transition"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-submit"
                className="bg-ess-emerald text-white px-6 py-4 text-[11px] uppercase tracking-[0.24em] font-semibold hover:bg-ess-emerald-dark transition disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Download Now"}
              </button>
            </form>
            <p className="mt-4 text-xs text-ess-slate/70">
              We respect your inbox. Unsubscribe with one click anytime.
            </p>
          </div>
        </div>
      </section>

      {/* ORGANIZATIONAL DEVELOPMENT */}
      <section className="ess-section bg-white" data-testid="home-org-dev">
        <div className="ess-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="ess-overline">Our Stage of Development</p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl text-ess-emerald leading-tight">
              Building Something Bigger Than A Business.
            </h2>
            <span className="ess-divider mt-6" />
          </div>
          <div className="lg:col-span-7 space-y-5 text-ess-charcoal/85 leading-relaxed">
            <p>
              The Exit Strategy Society is currently in its foundational growth phase.
            </p>
            <p>
              We are actively building educational resources, professional
              development programs, digital tools, workshops, and strategic
              partnerships that support professionals seeking greater freedom,
              flexibility, and opportunity.
            </p>
            <p>
              Every resource, product, workshop, and future initiative is designed
              to help individuals create sustainable pathways toward personal and
              economic empowerment.
            </p>
            <p className="font-display text-xl text-ess-emerald italic leading-snug">
              We are committed to growing with integrity, transparency, and
              measurable impact.
            </p>
          </div>
        </div>
      </section>

      {/* PARTNERSHIPS / GRANT */}
      <section className="ess-section bg-ess-paper">
        <div className="ess-container grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="ess-overline">Partnerships</p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl text-ess-emerald leading-tight">
              Partner with The Exit Strategy Society.
            </h2>
            <p className="mt-5 text-ess-slate leading-relaxed">
              We collaborate with corporations, foundations, community
              organizations, and educational institutions to expand access to
              economic empowerment and workforce development.
            </p>
            <ul className="mt-8 grid grid-cols-2 gap-3 text-sm text-ess-charcoal">
              {[
                "Corporate Partnerships",
                "Community Collaborations",
                "Sponsorships",
                "Educational Initiatives",
                "Workforce Development",
                "Speaking Engagements",
              ].map((x) => (
                <li key={x} className="flex items-center gap-2">
                  <Users size={14} className="text-ess-gold-deep" />
                  {x}
                </li>
              ))}
            </ul>
            <EmeraldButton
              as="link"
              to="/partnerships"
              data-testid="home-partner-cta"
              className="mt-8"
            >
              Become a Partner <ArrowRight size={14} className="ml-3" />
            </EmeraldButton>
          </div>
          <div className="bg-ess-emerald text-white p-10 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 ess-grain opacity-20" />
            <p className="ess-overline text-ess-gold relative">Grant Readiness</p>
            <h3 className="relative font-display mt-4 text-3xl md:text-4xl leading-tight">
              Supporting Education, Workforce Development, and Economic Opportunity.
            </h3>
            <p className="relative mt-5 text-white/80 leading-relaxed">
              The Exit Strategy Society seeks to expand access to education,
              professional development, digital literacy, entrepreneurship
              education, and financial wellness resources.
            </p>
            <p className="relative mt-4 text-white/80 leading-relaxed">
              We welcome opportunities to collaborate with foundations,
              corporations, community organizations, educational institutions,
              and workforce development initiatives that share our commitment to
              economic empowerment and lifelong learning.
            </p>
            <Link
              to="/impact"
              data-testid="home-grant-cta"
              className="relative mt-8 inline-flex items-center text-ess-gold text-[12px] uppercase tracking-[0.24em] ess-link"
            >
              Explore Partnership Opportunities
              <ArrowRight size={14} className="ml-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* LOOKING AHEAD — FUTURE IMPACT STATEMENT */}
      <section
        className="ess-section bg-ess-blush"
        data-testid="home-looking-ahead"
      >
        <div className="ess-container max-w-4xl mx-auto text-center">
          <Globe2 size={28} className="mx-auto text-ess-gold-deep" strokeWidth={1.4} />
          <p className="ess-overline mt-6">Looking Ahead</p>
          <h2 className="font-display mt-4 text-4xl md:text-5xl text-ess-emerald leading-tight">
            Committed to measurable, meaningful impact.
          </h2>
          <span className="ess-divider mt-6" />
          <div className="mt-8 space-y-5 text-ess-charcoal/85 leading-relaxed text-left md:text-center">
            <p>
              As The Exit Strategy Society grows, we are committed to measuring
              and reporting meaningful outcomes related to education, professional
              development, digital literacy, entrepreneurship, and community impact.
            </p>
            <p>
              Our goal is to build programs and resources that create measurable
              improvements in the lives of the professionals we serve.
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="ess-section bg-ess-emerald-dark text-white text-center">
        <div className="ess-container">
          <p className="ess-overline text-ess-gold">Begin</p>
          <h2 className="font-display mt-4 text-5xl md:text-6xl leading-tight">
            Let's build your exit strategy.
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-white/75">
            A private consultation with Dr. Foster is the first step toward the
            life your work has earned you.
          </p>
          <div className="mt-10">
            <GoldButton
              data-testid="home-final-book"
              onClick={() => (window.location.href = "/book")}
            >
              Book Your Consultation
            </GoldButton>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
